import { useCallback, useEffect } from "react";
import { pick } from "lodash";
import { Button, Input } from "@chakra-ui/react";
import { v1 as uuidv1 } from "uuid";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { assertBrandId, assertPresence } from "utils/assertions";
import { addNewRecordsWithFieldSpec } from "utils/apollo/cacheHelpers";
import { useBrandMutation, useBrandQuery } from "utils/apollo/hooks/brand";
import { useCurrentBrandId } from "utils/currentUserHooks";
import Form from "components/Shared/Forms/Form";
import useCreateValidSubmitHandler from "components/Shared/Forms/useCreateValidSubmitHandler";
import createFormFieldComponents from "components/Shared/Forms/FormField";
import InlineFieldsWrapper from "components/Shared/Forms/InlineFieldsWrapper";
import {
  makePostgraphileErrorProcessor,
  getPostgraphileErrorMatchersForFields,
} from "utils/PostgraphileErrorExtensions";
import NumberInput from "components/Shared/Forms/NumberInput";
import SwitchInput from "components/Shared/Forms/SwitchInput";
import { DocumentType, getFragmentData, gql } from "__generated__/gql";
import { ProductLineInput } from "__generated__/gql/graphql";
import type { Writeable } from "utils/typescriptUtilis";
import { trackEvent } from "utils/segment";
import { formatInteger, makeFormatCurrency } from "utils/formatting";
import * as productLineSchemaFields from "utils/schemas/productLine";
import { updateCachedProductLineSkus } from "components/Products/queries";
import NamingOptions from "components/Products/NamingOptions";
import EditPanelContent, {
  SaveButton,
} from "components/Products/EditPanelContent";
import {
  UNARCHIVED_STATUSES,
  getArchivedSkuFilter,
} from "components/Products/utils";
import DeleteProductLineButton, {
  DeleteProductLineData,
} from "./DeleteProductLineButton";
import ProductCategoriesSelect from "./ProductCategoriesSelect";
import ArchiveProductLineButton, {
  OnArchiveSuccess,
} from "./ArchiveProductLineButton";
import CalculatedField from "./CalculatedField";

const FORM_ID = "product-line-form";

const productLineFieldsFragment = gql(/* GraphQL */ `
  fragment ProductLineFields on ProductLine {
    id
    name
    shortName
    nameSeparator
    isInternalSkuIdAppended
    isArchived
    wholesaleUnitPrice
    shipsFromZip
    innerCaseQuantity
    wholesaleCaseQuantity
    productLineCategoryDepartmentSubDepartmentsList(orderBy: CREATED_AT_ASC) {
      id
      productCategoryDepartmentSubDepartment {
        id
        ...ProductCategoriesSelectOption
      }
    }
    skus {
      totalCount
    }
    ...ArchiveProductLineButtonProductLine
  }
`);

const productLineQuery = gql(/* GraphQL */ `
  query ProductLine($productLineId: UUID!) {
    productLine(id: $productLineId) {
      ...ProductLineFields
    }
  }
`);

const updateProductLineMutation = gql(/* GraphQL */ `
  mutation UpdateProductLine(
    $productLineId: UUID!
    $productLineData: ProductLinePatch!
    $productCategoryIds: [UUID!]!
    $hasUnitPriceUpdate: Boolean = false
  ) {
    updateProductLine(input: { id: $productLineId, patch: $productLineData }) {
      productLine {
        id
      }
    }
    setProductCategoryDepartmentSubDepartmentsForProductLine(
      input: {
        productLineId: $productLineId
        productCategoryDepartmentSubDepartmentIds: $productCategoryIds
      }
    ) {
      productLine {
        ...ProductLineFields
        ...ProductLinesSkusTableFieldsFragment
        skusList @include(if: $hasUnitPriceUpdate) {
          ...ProductsTableSku
        }
      }
    }
  }
`);

const createProductLineMutation = gql(/* GraphQL */ `
  mutation CreateProductLine(
    $productLineId: UUID!
    $productLineData: ProductLineInput!
    $productCategoryIds: [UUID!]!
  ) {
    createProductLine(input: { productLine: $productLineData }) {
      productLine {
        id
      }
    }
    setProductCategoryDepartmentSubDepartmentsForProductLine(
      input: {
        productLineId: $productLineId
        productCategoryDepartmentSubDepartmentIds: $productCategoryIds
      }
    ) {
      productLine {
        id
        name
        ...ProductLineFields
        ...ProductLinesSkusTableFieldsFragment
      }
    }
  }
`);

const productLineFields = [
  "name",
  "shortName",
  "nameSeparator",
  "isInternalSkuIdAppended",
  "wholesaleUnitPrice",
  "shipsFromZip",
  "innerCaseQuantity",
  "wholesaleCaseQuantity",
] as const;

type ProductLinePrimitiveInputs = Required<
  Pick<Writeable<ProductLineInput>, typeof productLineFields[number]>
>;

export type ProductLineInputs = ProductLinePrimitiveInputs & {
  productCategoryIds?: string[];
};

const productLineSchema: yup.ObjectSchema<ProductLineInputs> = yup
  .object()
  .shape({
    name: productLineSchemaFields.name,
    shortName: productLineSchemaFields.shortName.defined(),
    nameSeparator: productLineSchemaFields.nameSeparator.defined(),
    isInternalSkuIdAppended:
      productLineSchemaFields.isInternalSkuIdAppended.defined(),
    productCategoryIds: productLineSchemaFields.productCategoryIds,
    shipsFromZip: productLineSchemaFields.shipsFromZip,
    wholesaleUnitPrice: productLineSchemaFields.wholesaleUnitPrice,
    innerCaseQuantity: productLineSchemaFields.innerCaseQuantity,
    wholesaleCaseQuantity: productLineSchemaFields.wholesaleCaseQuantity,
  })
  .required();

const { FormField, ControlledFormField } =
  createFormFieldComponents<ProductLineInputs>({
    schema: productLineSchema,
  });

const processApolloError =
  makePostgraphileErrorProcessor<ProductLinePrimitiveInputs>(
    getPostgraphileErrorMatchersForFields(productLineFields, {
      type: "ProductLine",
    }),
  );

type Props = {
  isCreateNew?: boolean;
  onArchiveSuccess?: OnArchiveSuccess;
  onClose: () => void;
  onDeleteSuccess?: (data: DeleteProductLineData) => void;
  onNewCancel?: () => void;
  onNewSuccess?: (data: DocumentType<typeof createProductLineMutation>) => void;
  productLineId?: string;
};

export default function ProductLinePanel({
  isCreateNew = false,
  onArchiveSuccess,
  onClose,
  onDeleteSuccess,
  onNewCancel,
  onNewSuccess,
  productLineId,
}: Props) {
  const brandId = useCurrentBrandId();

  const formContext = useForm<ProductLineInputs>({
    resolver: yupResolver(productLineSchema),
    defaultValues: {
      nameSeparator: productLineSchemaFields.DEFAULT_NAME_SEPARATOR,
      isInternalSkuIdAppended: false,
    },
  });
  const {
    formState: { isDirty },
    reset,
  } = formContext;

  const { data, loading: isLoadingData } = useBrandQuery(productLineQuery, {
    skip: !productLineId || isCreateNew,
    variables: productLineId ? { productLineId } : undefined,
    returnPartialData: true,
  });

  const [updateProductLine, { loading: isSaving }] = useBrandMutation(
    updateProductLineMutation,
  );

  const [createProductLine, { loading: isCreating }] = useBrandMutation(
    createProductLineMutation,
    {
      onCompleted: (newProductLine) => {
        trackEvent("Product Line Created");
        if (onNewSuccess) onNewSuccess(newProductLine);
      },
      update: (cache, result) => {
        assertBrandId(brandId);

        const newProductLine = result.data?.createProductLine?.productLine;
        assertPresence(newProductLine);

        updateCachedProductLineSkus(
          cache,
          newProductLine,
          UNARCHIVED_STATUSES,
          (existing) => existing || [],
        );

        addNewRecordsWithFieldSpec({
          cache,
          baseFieldName: "productLines",
          newObjs: [newProductLine],
          getArgs: (existing) =>
            UNARCHIVED_STATUSES.map((status) => ({
              ...existing,
              condition: { brandId },
              filter: getArchivedSkuFilter(status),
            })),
        });
      },
    },
  );

  useEffect(() => {
    if (isCreateNew) formContext.setFocus("name");
  }, [formContext, isCreateNew]);

  useEffect(() => {
    if (isCreateNew) {
      reset({});
    } else if (data?.productLine) {
      const productLine = getFragmentData(
        productLineFieldsFragment,
        data.productLine,
      );
      reset({
        ...pick(productLine, productLineFields),
        productCategoryIds:
          productLine.productLineCategoryDepartmentSubDepartmentsList?.map(
            (c) => c.productCategoryDepartmentSubDepartment?.id,
          ),
      });
    }
  }, [data, isCreateNew, reset]);

  const hasUnitPriceUpdate =
    !!formContext.formState.dirtyFields.wholesaleUnitPrice;

  const [handleValidSubmit] = useCreateValidSubmitHandler(
    useCallback(
      async (
        formData: ProductLineInputs,
      ): Promise<{
        isCreated: boolean;
        productLine: DocumentType<typeof productLineFieldsFragment>;
      }> => {
        const { productCategoryIds = [], ...productLineData } = formData;

        if (productLineId && !isCreateNew) {
          const { data: updateData } = await updateProductLine({
            variables: {
              productLineId,
              productLineData,
              productCategoryIds,
              hasUnitPriceUpdate,
            },
          });
          const pl =
            updateData?.setProductCategoryDepartmentSubDepartmentsForProductLine
              ?.productLine;
          assertPresence(pl);
          return {
            isCreated: false,
            productLine: getFragmentData(productLineFieldsFragment, pl),
          };
        }

        assertBrandId(brandId);
        const newProductLineId = uuidv1();
        const { data: createData } = await createProductLine({
          variables: {
            productLineId: newProductLineId,
            productLineData: {
              ...productLineData,
              id: newProductLineId,
              brandId,
            },
            productCategoryIds,
          },
        });
        const pl =
          createData?.setProductCategoryDepartmentSubDepartmentsForProductLine
            ?.productLine;
        assertPresence(pl);
        return {
          isCreated: false,
          productLine: getFragmentData(productLineFieldsFragment, pl),
        };
      },
      [
        updateProductLine,
        productLineId,
        brandId,
        isCreateNew,
        createProductLine,
        hasUnitPriceUpdate,
      ],
    ),
    {
      getSuccessToast: (result) => ({
        title: `Product line ${
          result?.isCreated ? "created" : "updated"
        } successfully: ${result?.productLine.name}`,
      }),
      processApolloError,
    },
  );

  const productLine = getFragmentData(
    productLineFieldsFragment,
    data?.productLine,
  );

  const isWithoutSkus = productLine?.skus?.totalCount === 0;

  const [
    name,
    shortName,
    nameSeparator,
    isInternalSkuIdAppended,
    innerCaseQuantity,
    wholesaleCaseQuantity,
    wholesaleUnitPrice,
  ] = formContext.watch([
    "name",
    "shortName",
    "nameSeparator",
    "isInternalSkuIdAppended",
    "innerCaseQuantity",
    "wholesaleCaseQuantity",
    "wholesaleUnitPrice",
  ]);

  const wholesaleCaseQuantityUnits = wholesaleCaseQuantity
    ? (innerCaseQuantity ?? 1) * wholesaleCaseQuantity
    : null;

  return (
    <EditPanelContent
      onClose={onClose}
      description={`${isCreateNew ? "Add New" : "Edit"} Product Line`}
      heading={productLine?.name}
      headingPlaceholder={isCreateNew ? "New Product Line" : undefined}
      isArchived={productLine?.isArchived ?? undefined}
      isLoading={isLoadingData}
      footerContent={
        <>
          {onNewCancel && isCreateNew && (
            <Button onClick={onNewCancel} size="sm">
              Cancel
            </Button>
          )}
          {productLine && (productLine.isArchived || isWithoutSkus) && (
            <DeleteProductLineButton
              productLine={productLine}
              onCompleted={onDeleteSuccess}
            />
          )}
          {productLine && !isWithoutSkus && (
            <ArchiveProductLineButton
              productLine={productLine}
              onArchiveSuccess={onArchiveSuccess}
            />
          )}
          <SaveButton
            isLoading={isSaving || isCreating}
            isDisabled={!isCreating && !isDirty}
            form={FORM_ID}
          >
            {isCreateNew ? "Create" : "Save"}
          </SaveButton>
        </>
      }
    >
      <Form
        id={FORM_ID}
        formContext={formContext}
        onValidSubmit={handleValidSubmit}
        isLoaded={!isLoadingData || !productLineId}
      >
        <FormField name="name" id="ProductLinePanel-product-line-name" />

        <NamingOptions
          isInternalSkuIdAppended={isInternalSkuIdAppended ?? false}
          isLoading={isLoadingData}
          productLineName={name}
          productLineShortName={shortName}
          nameSeparator={nameSeparator}
        >
          <InlineFieldsWrapper mt={0}>
            <FormField name="shortName" placeholder={name || undefined} />

            <FormField
              name="nameSeparator"
              label="Separator"
              flex="0 0 6em !important"
              renderInput={(inputProps) => (
                <Input {...inputProps} fontWeight="bold" />
              )}
            />

            <ControlledFormField
              name="isInternalSkuIdAppended"
              renderInput={({ field }) => <SwitchInput {...field} />}
            />
          </InlineFieldsWrapper>
        </NamingOptions>

        <ControlledFormField
          name="productCategoryIds"
          renderInput={({ field }) => <ProductCategoriesSelect {...field} />}
        />

        <FormField
          name="shipsFromZip"
          id="ProductLinePanel-ships-from-zip"
          renderInput={(inputProps) => (
            <Input {...inputProps} pattern="[0-9]*" maxLength={5} />
          )}
        />

        <InlineFieldsWrapper>
          <ControlledFormField
            name="innerCaseQuantity"
            id="ProductLinePanel-inner-case-quantity"
            helpText="Number of units per inner case, if applicable, otherwise leave blank."
            renderInput={({ field }) => (
              <NumberInput
                {...field}
                max={32767}
                decimalScale={0}
                allowNegative={false}
                thousandSeparator
                placeholder="N/A"
              />
            )}
          />

          <ControlledFormField
            name="wholesaleCaseQuantity"
            id="ProductLinePanel-wholesale-case-quantity"
            helpText="Number of inner cases per master case, if applicable, or number of units if not."
            renderInput={({ field }) => (
              <NumberInput
                {...field}
                max={32767}
                decimalScale={0}
                thousandSeparator
                allowNegative={false}
              />
            )}
          />
        </InlineFieldsWrapper>

        <CalculatedField
          isLoading={isLoadingData}
          label={`Number of units per ${
            innerCaseQuantity ? "master " : ""
          }case`}
          value={formatInteger(wholesaleCaseQuantityUnits)}
        />

        <ControlledFormField
          name="wholesaleUnitPrice"
          id="ProductLinePanel-wholesaleUnitPrice"
          helpText={
            "Individual wholesale unit prices may be set separately for each SKU at each " +
            "wholesaler, but SKUs will default to this value where specific wholesale unit " +
            "prices are not set."
          }
          renderInput={({ field }) => (
            <NumberInput
              {...field}
              max={100000}
              decimalScale={2}
              fixedDecimalScale
              thousandSeparator
              prefix="$"
              allowNegative={false}
            />
          )}
        />

        {innerCaseQuantity && (
          <CalculatedField
            isLoading={isLoadingData}
            label="Default wholesale price per inner case"
            value={
              innerCaseQuantity && wholesaleUnitPrice
                ? makeFormatCurrency({
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })(innerCaseQuantity * parseFloat(wholesaleUnitPrice))
                : null
            }
          />
        )}

        <CalculatedField
          isLoading={isLoadingData}
          label={`Default wholesale price per ${
            innerCaseQuantity ? "master " : ""
          }case`}
          value={
            wholesaleCaseQuantityUnits && wholesaleUnitPrice
              ? makeFormatCurrency({
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })(wholesaleCaseQuantityUnits * parseFloat(wholesaleUnitPrice))
              : null
          }
        />
      </Form>
    </EditPanelContent>
  );
}
